import { useMemo } from "react";
import { Link } from "react-router-dom";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import AuditLogList from "@/pages/admin/audit/components/AuditLogList";
import MonthlyCleanupArchive from "@/pages/admin/audit/components/MonthlyCleanupArchive";
import { useAuditLogs } from "@/pages/admin/audit/hooks/useAuditLogs";

export default function AuditLogPage() {
  const { logs, loading, error, reload } = useAuditLogs();

  const stats = useMemo(() => {
    const success = logs.filter((log) => log.level === "success").length;
    const issues = logs.filter((log) => log.level === "warning" || log.level === "error").length;
    const actors = new Set(logs.map((log) => log.actor_name || "系统")).size;
    return { total: logs.length, success, issues, actors };
  }, [logs]);

  const cards = [
    { label: "日志总数", value: stats.total, icon: "ri-file-history-line", tone: "primary" },
    { label: "成功事件", value: stats.success, icon: "ri-checkbox-circle-line", tone: "primary" },
    { label: "告警 / 异常", value: stats.issues, icon: "ri-error-warning-line", tone: "accent" },
    { label: "涉及操作人", value: stats.actors, icon: "ri-group-line", tone: "secondary" },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-700",
    secondary: "bg-secondary-100 text-secondary-900",
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                首页
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <Link to="/admin" className="cursor-pointer transition-colors hover:text-foreground-950">
                管理后台
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">审计日志</span>
            </nav>
            <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className="ri-file-history-line" />
                  </span>
                  全链路操作留痕
                </span>
                <h1 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
                  审计日志
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  审批、发证、续期、吊销、账号停用与系统事件集中留痕，支持按级别、时间区间（含自定义）与关键词追溯，提供按操作人聚合、可切换近 7 天 / 近 30 天对比的操作量榜单，并可导出 CSV；批量解绑条目可展开回看本批次回收的设备，并提供按月份归档的「月度清理归档」按月翻查，顶部以柱状趋势展示近几个月清理情况（支持按台数 / 按次数切换口径，悬停即可查看当月各批次明细），支持按批次导出设备明细单独存档。
                </p>
              </div>
              <Link
                to="/admin"
                className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 md:w-auto"
              >
                <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                  <i className="ri-arrow-left-line" />
                </span>
                返回管理后台
              </Link>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((card) => (
              <div key={card.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-foreground-600">{card.label}</span>
                  <span className={`flex h-8 w-8 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
                    <i className={`${card.icon} text-base`} />
                  </span>
                </div>
                <p className="mt-2 font-heading text-2xl font-semibold text-foreground-950">
                  {loading ? "--" : card.value}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <MonthlyCleanupArchive logs={logs} loading={loading} />
          </div>

          <div className="mt-8">
            <AuditLogList logs={logs} loading={loading} error={error} onRetry={reload} />
          </div>
        </section>
      </main>

      <AppFooter />
    </div>
  );
}